/**
 * Card + card header, Row (the universal list item), IconTile, Avatar,
 * Skeleton (docs/04 §3). Flat surfaces — shadows are reserved for toasts and
 * dialogs (docs/04 §1).
 */

import { clsx } from 'clsx';
import type { HTMLAttributes, ReactNode } from 'react';

export function Card({ className, ...rest }: HTMLAttributes<HTMLDivElement>): ReactNode {
  return (
    <div
      className={clsx('rounded-card border border-card-line bg-card p-(--card-pad)', className)}
      {...rest}
    />
  );
}

export function CardHeader({
  title,
  action,
}: {
  readonly title: string;
  readonly action?: ReactNode;
}): ReactNode {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="font-display text-h2 text-ink">{title}</h2>
      {action}
    </div>
  );
}

/**
 * Row — icon, title + meta, right slot. Lists on phones are rows, never
 * shrunken tables (docs/03 §6).
 */
export function Row({
  icon,
  title,
  meta,
  right,
  as: As = 'div',
  href,
}: {
  readonly icon?: ReactNode;
  readonly title: ReactNode;
  readonly meta?: ReactNode;
  readonly right?: ReactNode;
  readonly as?: 'div' | 'a';
  readonly href?: string;
}): ReactNode {
  return (
    <As
      {...(As === 'a' ? { href } : {})}
      className={clsx(
        'flex min-h-(--row-min-h) items-center gap-3 border-b border-hairline py-(--row-py) last:border-b-0',
        As === 'a' && 'no-underline hover:bg-surface-hover',
      )}
    >
      {/* The catalogued tile, not a local copy of it (punch 5.4). This slot WAS
          the copy — a 36px centred rounded box with the neutral surface pair,
          which is the icon tile in every respect except being named one. */}
      {icon !== undefined ? <IconTile icon={icon} /> : null}
      <span className="min-w-0 grow">
        <span className="block truncate text-body font-medium text-ink">{title}</span>
        {meta !== undefined ? (
          <span className="block truncate text-meta text-ink2">{meta}</span>
        ) : null}
      </span>
      {right !== undefined ? (
        <span className="shrink-0 text-right text-meta text-ink2">{right}</span>
      ) : null}
    </As>
  );
}

/**
 * The semantic tones a tile may carry (docs/04 §3a). `neutral` is the default.
 *
 * The names are MetricBar's `MetricTone` names, plus the two the prototype's
 * tile uses that a filter strip has no need of (`brand`, `accent`). One
 * vocabulary across the design system, rather than a second set of words for
 * the same five ideas — which is the "five names for one thing" problem this
 * repo keeps finding, and it is no better one component at a time.
 */
export type IconTileTone = 'neutral' | 'brand' | 'accent' | 'ok' | 'warn' | 'bad' | 'info';

/**
 * 36px in a row, 48px at the top of a page or panel.
 *
 * Both sit on the 4px grid (§0 register). The prototype's tile is 38px, which
 * does not — 36 is the nearest grid step and matches the box this component
 * replaced in `Row`.
 */
export type IconTileSize = 'md' | 'lg';

/**
 * Every tone is a token PAIR that `scripts/ci/contrast.ts` already asserts in
 * both themes — 'ink2 on surface-hover', 'brand label on primary', 'gold chip',
 * 'ok chip', 'amber chip', 'red chip', 'info chip'. No tone introduces a pair
 * the contrast gate has not measured, which is why 5.4 needed no palette
 * change: the tile is a new SHAPE, not a new colour.
 */
const TONE: Record<IconTileTone, string> = {
  neutral: 'bg-surface-hover text-ink2',
  brand: 'bg-brand-700 text-on-brand',
  accent: 'bg-gold-tint text-gold-text',
  ok: 'bg-ok-bg text-ok-fg',
  warn: 'bg-amber-bg text-amber-fg',
  bad: 'bg-red-bg text-red-fg',
  info: 'bg-info-bg text-info-fg',
};

const SIZE: Record<IconTileSize, string> = {
  md: 'size-9',
  lg: 'size-12',
};

/**
 * The class list for a tile — exported so the mapping is unit-testable without
 * a DOM (the web unit suite runs in node). `IconTile.test.ts` is what makes an
 * unmeasured or non-token pair a FAILURE; without it a wrong tone renders as a
 * plausible-looking box and nothing complains.
 */
export function iconTileClass(tone: IconTileTone = 'neutral', size: IconTileSize = 'md'): string {
  return clsx('flex shrink-0 items-center justify-center rounded-card', SIZE[size], TONE[tone]);
}

/**
 * IconTile — the glyph in a soft tile (docs/04 §3a, punch item 5.4).
 *
 * The last catalogued component with no implementation, and the file said so:
 * this docblock has named `IconTile` since the day it was written, while the
 * only tile in it was an anonymous `<span>` inside `Row`. A second copy had
 * grown in the auth frame by the time it was built, with a glyph two sizes
 * outside the register's band. That is the shape gate 15 documents that it
 * CANNOT catch: a copy under a different name, or — as here — under none.
 *
 * The prototype's other three tiles (stat cards, needs-attention rows,
 * activity rows) have no v2 screen yet; adopting it there belongs to whichever
 * item builds those surfaces, not to this one.
 *
 * **The glyph is the caller's**, exactly as it is for `Row`, `StateShell` and
 * the notification drawer. The tile owns its BOX — size, radius, tone — and
 * docs/04 §3a states the glyph size that belongs in each box (18px in `md`,
 * 21px in `lg`, inside §6's 16–21px band and the standard's 16–24px). A tile
 * that took a component reference instead would own the size, and would also
 * be the one icon slot in the system with a different API.
 *
 * **Tone is never the only carrier of meaning** (docs/03 §8, §0 register). A
 * decorative tile is `aria-hidden` and the row's own words say what it is; a
 * tile that is genuinely the only place a word could live takes `label`, which
 * renders for assistive technology. There is no third option where a colour
 * alone means something.
 */
export function IconTile({
  icon,
  tone,
  size,
  label,
  className,
}: {
  readonly icon: ReactNode;
  readonly tone?: IconTileTone | undefined;
  readonly size?: IconTileSize | undefined;
  /**
   * Screen-reader text, when the tile carries meaning nothing beside it
   * carries. Omit it and the tile is decorative — which is the common case,
   * because a row that needs its icon read aloud usually needs a word on
   * screen too.
   */
  readonly label?: string | undefined;
  /** Layout only — position and margin. A caller may not restyle the tile. */
  readonly className?: string | undefined;
}): ReactNode {
  return (
    <span
      aria-hidden={label === undefined ? true : undefined}
      className={clsx(iconTileClass(tone, size), className)}
    >
      {icon}
      {label === undefined ? null : <span className="sr-only">{label}</span>}
    </span>
  );
}

export function Avatar({ name }: { readonly name: string }): ReactNode {
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();
  return (
    <span
      aria-hidden
      className="flex size-8 items-center justify-center rounded-full bg-brand-600 text-chip font-semibold text-on-brand"
    >
      {initials}
    </span>
  );
}

/** Skeletons match the final layout — no spinner-only pages (docs/03 §3). */
export function SkeletonRow(): ReactNode {
  return (
    <div
      aria-hidden
      className="flex items-center gap-3 border-b border-hairline py-(--row-py) last:border-b-0"
    >
      <span className="size-9 shrink-0 animate-pulse rounded-ctl bg-hairline" />
      <span className="grow space-y-2">
        <span className="block h-3.5 w-2/5 animate-pulse rounded bg-hairline" />
        <span className="block h-3 w-1/4 animate-pulse rounded bg-hairline" />
      </span>
    </div>
  );
}

/**
 * The loading placeholder for the BODY of a card that already exists.
 *
 * `SkeletonCard` renders its own `<Card>`, so putting one inside a card draws a
 * card inside a card: doubled border, doubled padding, and a visible collapse
 * to a single card the moment the data arrives — a layout jump on exactly the
 * screens where somebody is already waiting. Three sites did this (docs/03 §3).
 *
 * Use this one when the card, its header, or its filters are already on screen
 * and only the contents are still loading. Use `SkeletonCard` when the card
 * ITSELF is what has not arrived.
 *
 * It carries the `role="status"` rather than leaving it to the caller: the
 * announcement is the half a bare row cannot provide, and it is the half a
 * hurried replacement would drop.
 */
export function SkeletonRows({ rows = 3 }: { readonly rows?: number }): ReactNode {
  return (
    <div role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <SkeletonRow key={i} />
      ))}
    </div>
  );
}

export function SkeletonCard({ rows = 3 }: { readonly rows?: number }): ReactNode {
  return (
    <Card role="status" aria-label="Loading">
      {/* The title bar a real card would have — this is standing in for a whole
          card, so leaving it out makes the skeleton the wrong height. */}
      <span className="mb-3 block h-4 w-1/3 animate-pulse rounded bg-hairline" />
      {Array.from({ length: rows }, (_, i) => (
        <SkeletonRow key={i} />
      ))}
    </Card>
  );
}
